/* Amoss — shared section behaviour: scroll reveal + click-to-play video facades */
(function () {
  'use strict';

  document.documentElement.classList.remove('amoss-no-js');
  /* A stamp so a console check can tell which copy of this file is live,
     rather than guessing whether an upload took. */
  document.documentElement.setAttribute('data-amoss-js', '2026-09-24-f');

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function revealAll(scope) {
    var items = (scope || document).querySelectorAll('[data-amoss-reveal]');
    for (var i = 0; i < items.length; i++) items[i].classList.add('is-visible');
  }

  /* Wait until images and fonts have landed before deciding what is on screen.
     At DOMContentLoaded the page is still short, so every section would look
     like it was in view and would be revealed with no animation at all.
     The timeout is a backstop in case one slow image never finishes. */
  function whenSettled(callback) {
    var ran = false;
    function run() {
      if (ran) return;
      ran = true;
      requestAnimationFrame(function () {
        requestAnimationFrame(callback);
      });
    }

    if (document.readyState === 'complete') {
      run();
    } else {
      window.addEventListener('load', run);
      setTimeout(run, 1500);
    }
  }

  function observe(items, stagger, revealOnLoad) {
    for (var i = 0; i < items.length; i++) {
      items[i].style.setProperty('--am-anim-delay', i * stagger + 'ms');
    }

    /* The first screen (header + hero) plays on load, staggered, so nothing in
       it sits waiting for a scroll that never comes. is-entering runs the
       keyframe version, which plays even if the stylesheet lands late.
       Everything else is left to the observer below. */
    if (revealOnLoad) {
      for (var r = 0; r < items.length; r++) {
        items[r].classList.add('is-entering');
        items[r].classList.add('is-visible');
      }
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        for (var e = 0; e < entries.length; e++) {
          if (!entries[e].isIntersecting) continue;
          entries[e].target.classList.add('is-visible');
          obs.unobserve(entries[e].target);
        }
      },
      /* Trigger when the item's top passes 80% of the window height, not the
         moment it touches the bottom edge. Otherwise short things like a
         heading finish animating down in the corner of your eye and look
         static by the time you read them. threshold 0 so tall panels and
         short headings behave the same. */
      { rootMargin: '0px 0px -20% 0px', threshold: 0 }
    );

    for (var j = 0; j < items.length; j++) observer.observe(items[j]);
  }

  function setup(scope) {
    var root = scope || document;
    var sections = root.querySelectorAll('.amoss-section');

    if (reduced || !('IntersectionObserver' in window)) {
      revealAll(root);
      return;
    }

    /* Which sections count as "the first screen". Decided by position in the
       page, not by measuring heights: at this point images and web fonts have
       not loaded, so the page is shorter than it will be and measuring would
       wrongly count later sections as being on screen. */
    var firstScreenDone = false;

    for (var s = 0; s < sections.length; s++) {
      var section = sections[s];
      if (section.dataset.amossReady === 'true') continue;
      section.dataset.amossReady = 'true';

      var stagger = parseInt(section.dataset.amossStagger || '0', 10);
      var items = section.querySelectorAll('[data-amoss-reveal]');

      var revealOnLoad = false;
      if (!scope) {
        if (section.closest('.amoss-header-wrapper')) {
          revealOnLoad = true;
        } else if (!firstScreenDone) {
          firstScreenDone = true;
          revealOnLoad = section.getBoundingClientRect().top < window.innerHeight;
        }
      }

      (function (theseItems, thisStagger, revealNow) {
        if (revealNow) {
          /* two frames, so the hidden state is painted before it changes and
             the transition actually runs */
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              observe(theseItems, thisStagger, true);
            });
          });
          return;
        }

        whenSettled(function () { observe(theseItems, thisStagger, false); });
      })(items, stagger, revealOnLoad);
    }
  }

  function playFacade(button) {
    var wrap = button.closest('[data-amoss-video]');
    if (!wrap) return;
    var src = wrap.getAttribute('data-amoss-video');
    if (!src) return;
    var frame = document.createElement('iframe');
    frame.setAttribute('src', src + (src.indexOf('?') > -1 ? '&' : '?') + 'autoplay=1');
    frame.setAttribute('title', wrap.getAttribute('data-amoss-video-title') || 'Video');
    frame.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
    frame.setAttribute('allowfullscreen', 'true');
    wrap.innerHTML = '';
    wrap.appendChild(frame);
  }

  /* Audio previews: one plays at a time, button shows play / pause */
  function toggleAudio(button) {
    var row = button.parentElement;
    if (!row) return;
    var audio = row.querySelector('audio');
    if (!audio) return;

    var playing = document.querySelectorAll('audio');
    for (var i = 0; i < playing.length; i++) {
      if (playing[i] === audio) continue;
      if (playing[i].paused) continue;
      playing[i].pause();
      var other = playing[i].parentElement && playing[i].parentElement.querySelector('[data-amoss-audio]');
      if (other) other.classList.remove('is-playing');
    }

    if (audio.paused) {
      var started = audio.play();
      if (started && started.catch) started.catch(function () {});
      button.classList.add('is-playing');
    } else {
      audio.pause();
      button.classList.remove('is-playing');
    }

    if (!audio.dataset.amossBound) {
      audio.dataset.amossBound = 'true';
      audio.addEventListener('ended', function () { button.classList.remove('is-playing'); });
      audio.addEventListener('pause', function () { button.classList.remove('is-playing'); });
      audio.addEventListener('play', function () { button.classList.add('is-playing'); });
    }
  }

  /* ------------------------------------------------------------- pinning ---
     A pinned section sticks to the screen while the page scrolls past it.
     How far through you are becomes --am-progress (0 to 1) on the wrapper,
     which drives the growing bar, and reveals one step at a time.

     Progress only ever goes up. Scrolling back up leaves the sequence
     finished rather than rewinding it.
     ------------------------------------------------------------------------ */
  var pins = [];
  var pinTicking = false;

  function measurePins() {
    for (var i = 0; i < pins.length; i++) {
      pins[i].travel = pins[i].el.offsetHeight - pins[i].inner.offsetHeight;
    }
    updatePins();
  }

  function updatePins() {
    for (var i = 0; i < pins.length; i++) {
      var pin = pins[i];

      var progress = 1;
      if (pin.travel > 0) {
        progress = -pin.el.getBoundingClientRect().top / pin.travel;
      }
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;

      /* the high-water mark: never go backwards */
      if (progress <= pin.max) continue;
      pin.max = progress;

      pin.el.style.setProperty('--am-progress', progress.toFixed(4));

      var reached = Math.floor(progress * pin.steps.length + 0.0001);
      for (var s = 0; s < pin.steps.length; s++) {
        if (s <= reached) pin.steps[s].classList.add('is-shown');
      }

      if (progress > 0.999) pin.el.classList.add('is-complete');
    }
  }

  function finishPin(el) {
    el.classList.add('is-off', 'is-complete');
    el.style.setProperty('--am-progress', '1');
    var steps = el.querySelectorAll('[data-amoss-pin-step]');
    for (var i = 0; i < steps.length; i++) steps[i].classList.add('is-shown');
  }

  function setupPins(scope) {
    var nodes = (scope || document).querySelectorAll('[data-amoss-pin]');

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.dataset.amossPinReady === 'true') continue;
      el.dataset.amossPinReady = 'true';

      var inner = el.querySelector('[data-amoss-pin-inner]');

      /* no inner, reduced motion, or in the theme editor: show it all at once
         and drop the extra scroll height. Themes put the design-mode class on
         either html or body, so check both. */
      var designMode =
        document.documentElement.classList.contains('shopify-design-mode') ||
        (document.body && document.body.classList.contains('shopify-design-mode'));

      if (!inner || reduced || designMode) {
        finishPin(el);
        continue;
      }

      el.style.setProperty('--am-progress', '0');
      pins.push({ el: el, inner: inner, steps: el.querySelectorAll('[data-amoss-pin-step]'), max: -1, travel: 0 });
    }

    measurePins();
  }

  window.addEventListener('scroll', function () {
    if (pinTicking || !pins.length) return;
    pinTicking = true;
    requestAnimationFrame(function () {
      updatePins();
      pinTicking = false;
    });
  }, { passive: true });

  window.addEventListener('resize', measurePins);

  /* Tabs: a group is [data-amoss-tabs], buttons carry [data-amoss-tab="key"]
     and panels [data-amoss-panel="key"]. Used by the back catalogue's years.
     With no JS every panel shows, so nothing is ever unreachable. */
  function selectTab(button) {
    var group = button.closest('[data-amoss-tabs]');
    if (!group) return;
    var key = button.getAttribute('data-amoss-tab');

    /* In a toggle group, clicking the tab that is already open closes it, so
       the section can sit with nothing open at all. */
    var closing =
      group.hasAttribute('data-amoss-tabs-toggle') && button.classList.contains('is-active');

    var tabs = group.querySelectorAll('[data-amoss-tab]');
    for (var i = 0; i < tabs.length; i++) {
      var on = !closing && tabs[i] === button;
      tabs[i].classList.toggle('is-active', on);
      tabs[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      if (tabs[i].hasAttribute('aria-expanded')) {
        tabs[i].setAttribute('aria-expanded', on ? 'true' : 'false');
      }
    }

    var panels = group.querySelectorAll('[data-amoss-panel]');
    for (var j = 0; j < panels.length; j++) {
      panels[j].classList.toggle(
        'is-active',
        !closing && panels[j].getAttribute('data-amoss-panel') === key
      );
    }

    /* the group knows whether anything is open, so a hint line can hide */
    group.classList.toggle('is-open', !closing);
  }

  /* Accordions: a <details name="..."> group already opens one at a time in
     newer browsers. This does the same job in the ones that don't, and is
     harmless in the ones that do. The toggle event doesn't bubble, so it is
     caught on the way down instead. */
  document.addEventListener('toggle', function (event) {
    var el = event.target;
    if (!el || !el.open || !el.getAttribute) return;

    var group = el.getAttribute('data-amoss-exclusive');
    if (!group) return;

    var others = document.querySelectorAll('details[data-amoss-exclusive="' + group + '"]');
    for (var i = 0; i < others.length; i++) {
      if (others[i] !== el && others[i].open) others[i].open = false;
    }
  }, true);

  document.addEventListener('click', function (event) {
    var tab = event.target.closest('[data-amoss-tab]');
    if (tab) {
      event.preventDefault();
      selectTab(tab);
      return;
    }

    var button = event.target.closest('[data-amoss-play]');
    if (button) {
      event.preventDefault();
      playFacade(button);
      return;
    }

    var audioButton = event.target.closest('[data-amoss-audio]');
    if (audioButton) {
      event.preventDefault();
      toggleAudio(audioButton);
    }
  });

  /* A sticky header stops sticking if any ancestor is a scroll container, and
     plenty of themes put overflow: hidden on a page wrapper to kill sideways
     scrolling. overflow: clip does the same job without creating a scroll
     container, so swap it and sticky works again. Real scrolling areas
     (auto / scroll) are left alone. */
  function unblockSticky() {
    var header = document.querySelector('.amoss-header-wrapper');
    if (!header) return;

    var node = header.parentElement;
    while (node && node !== document.documentElement) {
      var style = window.getComputedStyle(node);
      if (style.overflowX === 'hidden') node.style.overflowX = 'clip';
      if (style.overflowY === 'hidden') node.style.overflowY = 'clip';
      node = node.parentElement;
    }
  }

  /* Measure the Amoss header so a full-height hero can subtract it and still
     fit exactly one screen. Sections that don't use it simply ignore the var.
     In fixed mode the header is out of the flow, so the page is padded by the
     same amount to stop the first section sliding underneath it. */
  function measureHeader() {
    var header = document.querySelector('.amoss-header-wrapper');
    var height = header ? Math.round(header.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--am-header-h', height + 'px');

    var inner = header && header.querySelector('[data-amoss-header-mode]');
    var mode = inner ? inner.getAttribute('data-amoss-header-mode') : '';
    if (mode === 'fixed') document.body.style.paddingTop = height + 'px';
  }

  window.addEventListener('resize', measureHeader);
  if (window.ResizeObserver) {
    var headerEl = document.querySelector('.amoss-header-wrapper');
    if (headerEl) new ResizeObserver(measureHeader).observe(headerEl);
  }

  /* Safety net: anything still hidden once the page is scrolled to the bottom
     (a short last section can never reach the trigger line) is revealed. */
  function revealAtBottom() {
    var docHeight = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );
    if (docHeight <= window.innerHeight + 2) return;
    if (window.innerHeight + window.pageYOffset < docHeight - 2) return;
    revealAll(document);
  }

  window.addEventListener('scroll', revealAtBottom, { passive: true });
  window.addEventListener('resize', revealAtBottom);

  function start() {
    unblockSticky();
    measureHeader();
    setup();
    setupPins();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* fonts and images can change the header's height after first paint */
  window.addEventListener('load', function () { unblockSticky(); measureHeader(); });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(measureHeader);

  /* Shopify theme editor: re-run when a section is added or reloaded */
  /* a late-loading image can change a pinned section's height */
  window.addEventListener('load', measurePins);

  document.addEventListener('shopify:section:load', function (event) {
    unblockSticky();
    measureHeader();
    setup(event.target);
    setupPins(event.target);
    revealAll(event.target);
  });

  /* ----------------------------------------------------------- the cart ---
     The cart's quantity box comes with an Update button, because without
     script that button is the only way to save a change. With script the
     change saves itself, so the button is hidden and the form submitted on
     the way out of the field. Anyone with script off keeps the button and
     the cart still works. */
  function hideCartUpdate() {
    var buttons = document.querySelectorAll('[data-amoss-cart-update]');
    for (var i = 0; i < buttons.length; i++) buttons[i].hidden = true;
  }

  document.addEventListener('change', function (event) {
    var field = event.target;
    if (!field || !field.classList) return;
    if (field.name !== 'updates[]') return;
    var form = field.form;
    if (!form || !form.querySelector('[data-amoss-cart-update]')) return;
    /* a blank box means the person is mid-edit, not that they want zero */
    if (field.value === '') return;
    form.submit();
  });

  hideCartUpdate();
  document.addEventListener('shopify:section:load', hideCartUpdate);

  /* -------------------------------------------------- subscription plans ---
     The product form carries its own hidden selling_plan so the sale is
     recurring even if a subscription app's script never loads. When the app
     does load and adds its own plan control to the same form, two fields share
     one name and the cart takes whichever comes last — a coin flip, and on a
     product with more than one plan it can quietly override what the buyer
     picked. So the fallback stands down as soon as a real control appears.

     The app injects late and re-renders on variant changes, so this watches
     rather than checking once, and stops watching after fifteen seconds. */
  function retirePlanFallback() {
    var fallbacks = document.querySelectorAll('input[data-amoss-plan-fallback]');
    for (var i = 0; i < fallbacks.length; i++) {
      var field = fallbacks[i];
      var form = field.form;
      if (!form) continue;
      var named = form.querySelectorAll('[name="selling_plan"]');
      for (var j = 0; j < named.length; j++) {
        if (named[j] !== field) {
          field.parentNode.removeChild(field);
          break;
        }
      }
    }
  }


  /* The script may be parsed before the page's markup exists, so anything
     that reads the DOM waits for it. Without this, a look for the post's
     content finds nothing and never runs again. */
  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* ---------------------------------------------------- audio in a post ---
     A post written in the Shopify editor gets the browser's own audio player:
     a white lozenge that matches nothing on the page. Where a section asks for
     it, each one is wrapped in the same player the sample previews use, and
     the original is kept in the wrapper so the existing play / pause handler
     finds it exactly where it expects to. */
  function trackName(audio) {
    var given = audio.getAttribute('title') || audio.getAttribute('data-name');
    if (given) return given;
    var src = audio.currentSrc || audio.getAttribute('src') || '';
    var source = audio.querySelector('source');
    if (!src && source) src = source.getAttribute('src') || '';
    if (!src) return 'Audio';
    var file = src.split('?')[0].split('/').pop() || '';
    try { file = decodeURIComponent(file); } catch (e) {}
    file = file.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ').trim();
    if (!file) return 'Audio';
    return file.charAt(0).toUpperCase() + file.slice(1);
  }

  function clock(seconds) {
    if (!isFinite(seconds) || seconds < 0) return '';
    var m = Math.floor(seconds / 60);
    var s = Math.floor(seconds % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  function dressArticleAudio() {
    var hosts = document.querySelectorAll('[data-amoss-article-audio]');
    for (var h = 0; h < hosts.length; h++) {
      var tracks = hosts[h].querySelectorAll('audio');
      for (var i = 0; i < tracks.length; i++) {
        var audio = tracks[i];
        if (audio.dataset.amossDressed) continue;
        audio.dataset.amossDressed = 'true';

        var name = trackName(audio);

        var wrap = document.createElement('div');
        wrap.className = 'amoss-player';
        audio.parentNode.insertBefore(wrap, audio);

        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'amoss-player__button';
        button.setAttribute('data-amoss-audio', '');
        button.setAttribute('aria-label', 'Play ' + name);
        button.innerHTML =
          '<svg class="amoss-player__play" width="10" height="12" viewBox="0 0 10 12" aria-hidden="true">' +
          '<path d="M0 0 L10 6 L0 12 Z" fill="currentColor"></path></svg>' +
          '<svg class="amoss-player__pause" width="10" height="12" viewBox="0 0 10 12" aria-hidden="true">' +
          '<rect x="0" y="0" width="3.5" height="12" fill="currentColor"></rect>' +
          '<rect x="6.5" y="0" width="3.5" height="12" fill="currentColor"></rect></svg>';

        var label = document.createElement('span');
        label.className = 'amoss-player__name';
        label.textContent = name;

        var time = document.createElement('span');
        time.className = 'amoss-player__time';
        time.textContent = clock(audio.duration);

        wrap.appendChild(button);
        wrap.appendChild(label);
        wrap.appendChild(time);
        /* the audio moves inside the wrapper: toggleAudio looks for it as a
           sibling of the button, so it has to sit here and nowhere else */
        wrap.appendChild(audio);

        audio.removeAttribute('controls');
        audio.classList.add('is-dressed');
        audio.preload = 'metadata';

        (function (a, t) {
          a.addEventListener('loadedmetadata', function () { t.textContent = clock(a.duration); });
        })(audio, time);
      }
    }
  }

  onReady(dressArticleAudio);


  /* ------------------------------------------------ two to a row ---
     Videos in these posts are not next to each other in the markup: each one
     follows its own heading and description. So pairing the videos alone
     would tear them away from the words that explain them. Instead each
     heading and everything under it up to the next heading is gathered into
     one block, and the blocks are laid two to a row — the video comes along
     with its own title and text.

     Real headings are the reliable join. But Shopify's editor rewrites a
     pasted heading as a bold paragraph on this field, and posts written in the
     editor come out the same way, so "At every bold title" reads one of those
     as a join too. */
  /* A line that is doing a heading's job without being one: a short line whose
     words all sit inside bold or a link, with nothing else of substance in it.
     Length and the media check keep a body paragraph that merely starts with a
     link from being mistaken for a title. */
  function titleShaped(el) {
    if (!el || (el.tagName !== 'P' && el.tagName !== 'DIV')) return false;
    if (el.querySelector('iframe, img, audio, video, .amoss-player')) return false;

    var text = (el.textContent || '').replace(/ /g, ' ').trim();
    if (!text || text.length > 120) return false;
    if (!el.querySelector('strong, b, a')) return false;

    /* What is left once the bold and the links are taken out: punctuation and
       spaces are fine, any remaining words mean this is a sentence. */
    var probe = el.cloneNode(true);
    var marks = probe.querySelectorAll('strong, b, a');
    for (var m = 0; m < marks.length; m++) {
      if (marks[m].parentNode) marks[m].parentNode.removeChild(marks[m]);
    }
    return !/[a-z0-9]/i.test(probe.textContent || '');
  }

  /* Every join in the post, in the order they appear. A title inside one that
     has already been taken is skipped, so a wrapper never competes with the
     line it wraps. */
  function findTitles(host, level) {
    var out = [];
    var i;
    if (level !== 'BOLD') {
      var tagged = host.getElementsByTagName(level);
      for (i = 0; i < tagged.length; i++) out.push(tagged[i]);
      return out;
    }
    var all = host.querySelectorAll('h1, h2, h3, h4, p, div');
    for (i = 0; i < all.length; i++) {
      var el = all[i];
      if (!/^H[1-4]$/.test(el.tagName) && !titleShaped(el)) continue;
      var nested = false;
      for (var j = 0; j < out.length; j++) {
        if (out[j].contains(el)) { nested = true; break; }
      }
      if (!nested) out.push(el);
    }
    return out;
  }
  /* True for a video, picture or player, or anything holding one. Where this
     first comes true in a block is where its media begins. */
  function carriesMedia(el) {
    if (!el || !el.tagName) return false;
    var tag = el.tagName;
    if (tag === 'IFRAME' || tag === 'IMG' || tag === 'AUDIO' || tag === 'VIDEO') return true;
    return !!el.querySelector('iframe, img, audio, video, .amoss-player');
  }

  function groupArticleColumns() {
    var hosts = document.querySelectorAll('[data-amoss-article-columns]');
    for (var h = 0; h < hosts.length; h++) {
      var host = hosts[h];
      if (host.dataset.amossGrouped) continue;

      var level = host.getAttribute('data-amoss-article-columns') || 'H2';

      /* Taken as a fixed list before anything moves, and across the whole
         post rather than one wrapper at a time: the editor splits content
         into several divs, and grouping inside each one separately left the
         headings that happened to be alone in their div ungrouped. */
      var headings = findTitles(host, level);
      if (headings.length < 2) continue;

      /* Where a run ends is decided by this list rather than by tag name: in
         "At every bold title" the joins are ordinary paragraphs, and there is
         no tag that tells them apart from the paragraphs around them. */
      var isTitle = function (el) {
        for (var t = 0; t < headings.length; t++) if (headings[t] === el) return true;
        return false;
      };

      var grid = document.createElement('div');
      grid.className = 'amoss-article__grid';

      /* The grid goes in at the top level, where the first heading's own
         branch begins, so it spans the column rather than sitting inside a
         wrapper that may be narrower. */
      var top = headings[0];
      while (top.parentElement && top.parentElement !== host) top = top.parentElement;
      host.insertBefore(grid, top);

      for (var g = 0; g < headings.length; g++) {
        var heading = headings[g];
        /* Everything after the heading, up to the next one, belongs with it.
           Collected before the move, or the run is lost as soon as the
           heading leaves its parent. */
        var run = [heading];
        var sib = heading.nextElementSibling;
        while (sib && !isTitle(sib)) {
          run.push(sib);
          sib = sib.nextElementSibling;
        }
        var group = document.createElement('div');
        group.className = 'amoss-article__group';

        /* Three slots, always, whatever the run holds: the heading, the words
           under it, and the media at the end. A fixed three is what lets the
           blocks line their rows up with each other across the grid - with the
           run poured straight in, every block has a different number of parts
           and there is nothing for the row to line up on. Empty slots are kept
           for the same reason. */
        var head = document.createElement('div');
        head.className = 'amoss-article__group-head';
        var words = document.createElement('div');
        words.className = 'amoss-article__group-text';
        var media = document.createElement('div');
        media.className = 'amoss-article__group-media';

        head.appendChild(run[0]);
        var inMedia = false;
        for (var r = 1; r < run.length; r++) {
          if (!inMedia && carriesMedia(run[r])) inMedia = true;
          (inMedia ? media : words).appendChild(run[r]);
        }

        group.appendChild(head);
        group.appendChild(words);
        group.appendChild(media);
        grid.appendChild(group);
      }

      /* Wrappers the content has just left behind would otherwise sit there
         as empty boxes taking up space. Removing an inner one can leave its
         parent empty in turn, so this repeats until there are none left. */
      for (var pass = 0; pass < 5; pass++) {
        var leftovers = host.querySelectorAll('div:empty');
        var removed = 0;
        for (var e = 0; e < leftovers.length; e++) {
          /* Anything inside the grid is left alone: an empty slot is there on
             purpose, holding a row open so the blocks stay in step. */
          if (leftovers[e] === grid || grid.contains(leftovers[e])) continue;
          leftovers[e].parentNode.removeChild(leftovers[e]);
          removed++;
        }
        if (!removed) break;
      }

      host.dataset.amossGrouped = 'true';
    }
  }

  onReady(groupArticleColumns);

  /* ------------------------------------------- landing on an anchor ---
     A link from another page — /#whats-inside — is jumped to the moment the
     browser meets the target, while the page above it is still arriving. The
     hero's picture, the web fonts and everything else then load, grow, and
     push the section a long way down; the window is left near the top and the
     visitor lands on the hero instead of where they asked for.

     So the jump is made again once the page has settled. It gives up the
     moment the visitor scrolls themselves, because by then the position is
     theirs, not ours. scroll-margin-top on the sections keeps the heading
     clear of the fixed header, so plain scrollIntoView is enough. */
  function settleOnHash() {
    var hash = window.location.hash;
    if (!hash || hash.length < 2) return;

    var theirs = false;
    function handOver() { theirs = true; }
    window.addEventListener('wheel', handOver, { passive: true });
    window.addEventListener('touchmove', handOver, { passive: true });
    window.addEventListener('keydown', handOver);

    /* Instant rather than smooth: this is a correction to a jump that has
       already happened, and gliding the length of the page looks like a fault
       rather than a flourish. */
    /* The section is looked up here rather than above, because this file is
       loaded in the head and the page it is looking for does not exist yet
       when the script first runs. */
    function land() {
      if (theirs) return;

      var name = (window.location.hash || '').slice(1);
      if (!name) return;
      var target = document.getElementById(name);
      if (!target) {
        try { target = document.getElementById(decodeURIComponent(name)); } catch (e) { target = null; }
      }
      if (!target) return;

      /* Already in place, allowing for the room the section keeps clear of the
         fixed header, so a page that arrived correctly is left alone. */
      var clear = parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
      if (Math.abs(target.getBoundingClientRect().top - clear) < 4) return;

      target.scrollIntoView({ block: 'start', behavior: 'auto' });
    }

    function settle() {
      land();
      window.setTimeout(land, 300);
      window.setTimeout(land, 900);
    }

    if (document.readyState === 'complete') settle();
    else window.addEventListener('load', settle);
  }
  settleOnHash();

  onReady(function () {
    if (!document.querySelector('input[data-amoss-plan-fallback]')) return;
    retirePlanFallback();
    if (window.MutationObserver) {
      var planWatch = new MutationObserver(retirePlanFallback);
      planWatch.observe(document.documentElement, { childList: true, subtree: true });
      window.setTimeout(function () { planWatch.disconnect(); }, 15000);
    }
  });
})();
